import React, { useState, useMemo } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	Radio,
	RadioGroup,
	Typography,
	TextField
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import {
	createOrUpdateScheduledUpdate,
	deleteScheduledUpdate
} from '../../../../api/serverRequests';

const useStyles = makeStyles((theme) => ({
	formControl: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		display: 'flex',
		flexDirection: 'column'
	},
	button: {
		marginTop: theme.spacing(2)
	},
	cancelButton: {
		marginTop: theme.spacing(2),
		marginLeft: theme.spacing(1)
	},
	formContainer: {
		padding: theme.spacing(2),
		borderTop: `1px solid ${theme.palette.divider}`
	},
	title: {
		fontWeight: 500
	},
	datePicker: {
		width: '200px'
	},
	listingFiltersSection: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1)
	}
}));

export default function ScheduledUpdateSettings({ bookmark, onSave, onDelete, onCancel, onError }) {
	const classes = useStyles();
	const existingScheduledUpdate = bookmark?.ScheduledUpdate || bookmark?.scheduled_update || null;
	const [frequency, setFrequency] = useState(existingScheduledUpdate?.Frequency || 'weekly');
	const [startDate, setStartDate] = useState(
		existingScheduledUpdate?.StartDate ? moment(existingScheduledUpdate.StartDate) : moment()
	);

	const availableFilters = useMemo(() => {
		try {
			const searchQuery = bookmark?.SearchQuery || bookmark?.Result;
			if (!searchQuery) return { hasExpiredStatus: false, hasTerminatedStatus: false };

			const queryData = JSON.parse(searchQuery);
			const advFilters = queryData.advFilters || {};

			return {
				hasExpiredStatus: Boolean(advFilters.expiredStatus),
				hasTerminatedStatus: Boolean(advFilters.terminatedStatus)
			};
		} catch (error) {
			console.log('Error parsing bookmark search query:', error);
			return { hasExpiredStatus: false, hasTerminatedStatus: false };
		}
	}, [bookmark?.SearchQuery, bookmark?.Result]);

	const [filterMode, setFilterMode] = useState(() => {
		if (availableFilters.hasExpiredStatus && availableFilters.hasTerminatedStatus) {
			return 'both';
		} else if (availableFilters.hasExpiredStatus) {
			return 'expired';
		} else if (availableFilters.hasTerminatedStatus) {
			return 'terminated';
		}
		return 'all';
	});

	const handleSave = () => {
		createOrUpdateScheduledUpdate({
			bookmark_id: bookmark.Id,
			frequency,
			start_date: startDate.toISOString(),
			recently_expired: filterMode === 'expired' || filterMode === 'both',
			recently_terminated: filterMode === 'terminated' || filterMode === 'both'
		})
			.then((res) => {
				onSave?.(res.data.scheduled_update);
			})
			.catch(() => onError?.('Failed to save scheduled update'));
	};

	const handleDelete = () => {
		if (existingScheduledUpdate?.Id) {
			deleteScheduledUpdate(existingScheduledUpdate.Id)
				.then(() => {
					onDelete?.(bookmark.Id);
				})
				.catch(() => onError?.('Failed to delete scheduled update'));
		}
	};

	const showListingFilters =
		availableFilters.hasExpiredStatus || availableFilters.hasTerminatedStatus;

	return (
		<LocalizationProvider dateAdapter={AdapterMoment}>
			<Box className={classes.formContainer}>
				<Typography variant="subtitle1" gutterBottom className={classes.title}>
					Scheduled Update Settings
				</Typography>
				<FormControl component="fieldset" className={classes.formControl}>
					<FormLabel component="legend">Frequency</FormLabel>
					<RadioGroup
						row
						aria-label="frequency"
						name="frequency"
						value={frequency}
						onChange={(e) => setFrequency(e.target.value)}
					>
						<FormControlLabel
							value="weekly"
							control={<Radio color="primary" />}
							label="Weekly"
						/>
						<FormControlLabel
							value="biweekly"
							control={<Radio color="primary" />}
							label="Biweekly"
						/>
						<FormControlLabel
							value="monthly"
							control={<Radio color="primary" />}
							label="Monthly"
						/>
					</RadioGroup>
				</FormControl>
				<Grid container spacing={3} alignItems="flex-start">
					<Grid item>
						<DatePicker
							label="Start Date"
							value={startDate}
							onChange={setStartDate}
							renderInput={(params) => (
								<TextField {...params} size="small" variant="outlined" />
							)}
							className={classes.datePicker}
						/>
					</Grid>
					<Grid item>
						{showListingFilters && (
							<FormControl
								component="fieldset"
								className={classes.listingFiltersSection}
							>
								<FormLabel component="legend">Listing Filters</FormLabel>
								<RadioGroup
									aria-label="listing-filters"
									name="listing-filters"
									value={filterMode}
									onChange={(e) => setFilterMode(e.target.value)}
								>
									<Grid container spacing={0}>
										<Grid item xs={6}>
											<FormControlLabel
												value="all"
												control={<Radio color="primary" />}
												label="All"
											/>
										</Grid>
										{availableFilters.hasExpiredStatus && (
											<Grid item xs={6}>
												<FormControlLabel
													value="expired"
													control={<Radio color="primary" />}
													label="Recently Expired"
												/>
											</Grid>
										)}
										{availableFilters.hasTerminatedStatus && (
											<Grid item xs={6}>
												<FormControlLabel
													value="terminated"
													control={<Radio color="primary" />}
													label="Recently Terminated"
												/>
											</Grid>
										)}
										{availableFilters.hasExpiredStatus &&
											availableFilters.hasTerminatedStatus && (
												<Grid item xs={6}>
													<FormControlLabel
														value="both"
														control={<Radio color="primary" />}
														label="Recently Expired & Terminated"
													/>
												</Grid>
											)}
									</Grid>
								</RadioGroup>
							</FormControl>
						)}
					</Grid>
				</Grid>

				<Box mt={2}>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSave}
						className={classes.button}
					>
						{existingScheduledUpdate ? 'UPDATE' : 'SAVE'}
					</Button>
					{existingScheduledUpdate && (
						<Button
							variant="outlined"
							color="secondary"
							onClick={handleDelete}
							className={classes.cancelButton}
						>
							DELETE
						</Button>
					)}
					<Button variant="text" onClick={onCancel} className={classes.cancelButton}>
						CANCEL
					</Button>
				</Box>
			</Box>
		</LocalizationProvider>
	);
}
